/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState } from 'react';
import { ArrowRight } from '@carbon/icons-react';
import { InlineLoading } from '@carbon/react';

import { IdpProjectDocumentUpload } from 'icons';

import * as Styled from './DocumentUpload.styled';

/**
 * @typedef {Object} DocumentUploadLabels
 * @property {string} dropzoneLabelText
 * @property {string} titleText
 * @property {string} descriptionText
 * @property {string} extractButtonText
 * @property {string} extractingInProgressText
 *
 * @typedef {Object} DocumentUploadDocument
 * @property {string} id
 * @property {string} name
 *
 * @typedef {Object} DocumentUploadProps
 * @property {boolean} canUserModifyProject
 * @property {DocumentUploadDocument[]} documents
 * @property {() => void} onExtractClick
 * @property {boolean} [multiple]
 * @property {boolean} [isExtracting]
 * @property {DocumentUploadLabels} labels
 * @property {boolean} canProceedWithExtraction
 * @property {(files: File[]) => Promise<void>} onFilesUpload
 * @property {(documentId: string) => Promise<void>} onDeleteDocument
 */

/**
 * @param {DocumentUploadProps} props
 */
export const DocumentUpload = ({
  canUserModifyProject,
  documents = [],
  onExtractClick,
  multiple = false,
  isExtracting = false,
  labels,
  canProceedWithExtraction,
  onFilesUpload,
  onDeleteDocument
}) => {
  const [loading, setLoading] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);

  const handleFiles = async (_, { addedFiles }) => {
    if (addedFiles && addedFiles.length > 0) {
      setLoading(true);
      try {
        if (onFilesUpload) {
          await onFilesUpload(addedFiles);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatus = (document) => {
    if (isExtracting || loading) {
      return 'uploading';
    }
    if (deletingDocumentId === document.id) {
      return 'uploading';
    }
    if (!canUserModifyProject) {
      return 'complete';
    }
    return 'edit';
  };

  const handleDelete = async (documentId) => {
    if (isExtracting) {
      return;
    }
    setDeletingDocumentId(documentId);
    try {
      if (onDeleteDocument) {
        await onDeleteDocument(documentId);
      }
    } finally {
      setDeletingDocumentId(null);
    }
  };

  return (
    <Styled.Container>
      <Styled.Icon>
        <IdpProjectDocumentUpload width="101" height="108" />
      </Styled.Icon>
      <Styled.Content>
        <Styled.Title>{labels.titleText}</Styled.Title>
        <Styled.Description>{labels.descriptionText}</Styled.Description>
        <Styled.StyledFileUploaderDropContainer
          accept={['.pdf']}
          labelText={labels.dropzoneLabelText}
          multiple={multiple}
          disabled={loading || !canUserModifyProject || (!multiple && documents.length > 0)}
          onAddFiles={handleFiles}
        />
        {documents.length > 0 && (
          <Styled.FileListContainer>
            {documents.map((document) => (
              <Styled.StyledFileUploaderItem
                key={document.id}
                name={document.name}
                status={handleStatus(document)}
                iconDescription="File"
                onDelete={() => handleDelete(document.id)}
              />
            ))}
          </Styled.FileListContainer>
        )}
        {isExtracting ? (
          <Styled.LoadingContainer>
            <InlineLoading description={labels.extractingInProgressText} status="active" />
          </Styled.LoadingContainer>
        ) : (
          <Styled.StyledButton
            size="lg"
            onClick={onExtractClick}
            renderIcon={ArrowRight}
            disabled={loading || !canUserModifyProject || documents.length === 0 || !canProceedWithExtraction}
          >
            {labels.extractButtonText}
          </Styled.StyledButton>
        )}
      </Styled.Content>
    </Styled.Container>
  );
};
